import { useState } from 'react';
import { IconAlertTriangle, IconArrowsDiff, IconBox, IconCalendarEvent, IconCertificate, IconClockHour4, IconCrane, IconFileAlert, IconFileCheck, IconTruck, IconDownload, IconExternalLink, IconFileText, IconHistory, IconListDetails, IconLock, IconPlayerPlay, IconPlus, IconUpload, IconX, type Icon } from '@tabler/icons-react';
import { DataError, useList } from '@hana/data';
import { useT } from '@hana/i18n';
import { HeaderTool, Page, PageHeader, useLayout } from '@hana/shell';
import { Button, Segmented, Skeleton, StateView, useToast, type ScreenState } from '@hana/ui';
import { CALC, calcSur, codesOf, counts, cusRows, deltaText, dk, floorText, groupCount, PRICE_GROUPS, priceText, resolveCus, resolveLines, routeRows, SEAT, ST, stdRows, surRows, UNIT, vnd, type PriceFilter, type ShownSt } from './priceLogic';
import type { PriceGrp } from './priceTypes';
import { PriceCompact } from './PriceCompact';
import s from './price.module.css';
import './price.i18n';

// Mint-ServicePrice (1536 / 1280): Biểu giá dịch vụ — giá BÁN niêm yết. Chỉ đọc; nút ghi báo toast.
// Giá sàn chỉ hiện với vai Quản lý (board: Sale không thấy giá sàn). Không có giá mua / lãi.

type Tab = 'std' | 'cus' | 'sur' | 'trf';
const params = () => new URLSearchParams(typeof location === 'undefined' ? '' : location.search);
function readState(): ScreenState | null {
  const v = params().get('state');
  return v === 'loading' || v === 'empty' || v === 'error' || v === 'noperm' ? v : null;
}
const tile = (color: string) => ({ background: `color-mix(in srgb, ${color} 18%, var(--card))` });
const stOf = (k: ShownSt) => ({ color: ST[k][1], background: ST[k][2] });
const FILTER: Record<Exclude<PriceFilter, 'all'>, string> = { wait: 'prc.f.wait', exp: 'prc.f.exp', below: 'prc.f.below' };

function StChip({ k }: { k: ShownSt }) {
  return <span className={s.st} style={stOf(k)}>{ST[k][0]}</span>;
}
function Code({ code, grp, w = 54 }: { code: string; grp: PriceGrp; w?: number }) {
  const tr = useT();
  const g = PRICE_GROUPS.find((x) => x.k === grp)!;
  return <span className={s.code} style={{ width: w, ...tile(g.color) }} title={tr('prc.feeCode', { code })}>{code}</span>;
}

function PriceTable() {
  const tr = useT();
  const toast = useToast();
  const linesQ = useList('priceLine', { limit: 200 });
  const cusQ = useList('priceCus', { limit: 200 });
  const surQ = useList('priceSur', { limit: 200 });
  const rtQ = useList('priceRoute', { limit: 200 });
  const forced = readState();
  const [tab, setTab] = useState<Tab>('std');
  const [role, setRole] = useState<'sale' | 'mgr'>(params().get('role') === 'sale' ? 'sale' : 'mgr');
  const [grp, setGrp] = useState<PriceGrp | 'all'>('all');
  const [code, setCode] = useState('all');
  const [qf, setQf] = useState<PriceFilter>('all');
  const [all, setAll] = useState(false);
  const [ci, setCi] = useState({ h: '60', w: '3,5', wh: '1', ex: '250000' });
  const [calc, setCalc] = useState<ReturnType<typeof calcSur> | null>(null);

  let state: ScreenState = 'ready';
  if (forced) state = forced;
  else if (linesQ.isLoading || cusQ.isLoading || surQ.isLoading || rtQ.isLoading) state = 'loading';
  else if (linesQ.isError || cusQ.isError || surQ.isError || rtQ.isError) state = [linesQ.error, cusQ.error, surQ.error, rtQ.error].some((e) => e instanceof DataError && e.code === 'noperm') ? 'noperm' : 'error';
  else if ((linesQ.data?.items.length ?? 0) === 0) state = 'empty';
  const noperm = state === 'noperm';
  const ready = state === 'ready';
  const mgr = role === 'mgr';

  const lines = resolveLines(linesQ.data?.items ?? []);
  const cus = resolveCus(cusQ.data?.items ?? [], lines);
  const sc = { grp, code };
  const n = counts(lines, cus);
  const noWrite = () => toast({ msg: tr('prc.noWrite') });
  const soonTo = (what: string) => () => toast({ msg: tr('prc.soon', { what }) });
  const link = (what: string) => (e: { preventDefault: () => void; stopPropagation: () => void }) => {
    e.preventDefault();
    e.stopPropagation();
    soonTo(what)();
  };

  const pickGrp = (k: PriceGrp | 'all') => { setGrp(k); setCode('all'); };
  const pickTab = (k: string) => {
    setTab(k as Tab);
    if (k === 'trf') { setGrp('tr'); setCode('all'); }
    if (k === 'std' && qf === 'below') setQf('all');
  };
  const svName = grp === 'all' ? tr('prc.allSvc') : PRICE_GROUPS.find((g) => g.k === grp)!.name;
  const GICO: Record<PriceGrp, Icon> = { hq: IconFileCheck, kd: IconCertificate, tr: IconTruck, cg: IconCrane, kh: IconBox };
  const tree: Array<{ k: PriceGrp | 'all'; name: string; color: string; Ico: Icon }> = [{ k: 'all', name: tr('prc.allSvc'), color: 'var(--tx2)', Ico: IconListDetails }, ...PRICE_GROUPS.map((g) => ({ ...g, Ico: GICO[g.k] }))];

  const std = ready ? stdRows(lines, sc, qf === 'below' ? 'all' : qf, all) : [];
  const cusV = ready ? cusRows(cus, sc, qf) : [];
  const surV = ready ? surRows(surQ.data?.items ?? [], sc) : [];
  const trfV = ready ? routeRows(rtQ.data?.items ?? []) : [];
  const nRows = tab === 'std' ? std.length : tab === 'cus' ? cusV.length : tab === 'sur' ? surV.length : trfV.length;
  const byGroup = <T,>(rows: T[], g: (r: T) => PriceGrp) => PRICE_GROUPS.filter((x) => grp === 'all' || x.k === grp).map((x) => ({ ...x, rows: rows.filter((r) => g(r) === x.k) })).filter((x) => x.rows.length > 0);
  const setIn = (k: keyof typeof ci) => (e: { target: { value: string } }) => setCi({ ...ci, [k]: e.target.value });

  const colsStd = mgr ? s.cStdM : s.cStdS;
  const colsCus = mgr ? s.cCusM : s.cCusS;
  const colsTrf = mgr ? s.cTrfM : s.cTrfS;
  const chip = (k: Exclude<PriceFilter, 'all'>, num: number) => (
    <button key={k} type="button" className={`${s.mchip} ${s[`m_${k}`]} ${qf === k ? s.mchipOn : ''}`} aria-pressed={qf === k} aria-label={`${num} ${tr(FILTER[k]).toLowerCase()}`} title={tr(FILTER[k])} onClick={() => { setQf(qf === k ? 'all' : k); if (k === 'below') setTab('cus'); }}>
      {k === 'wait' ? <IconClockHour4 size={14} stroke={1.8} aria-hidden="true" /> : k === 'exp' ? <IconCalendarEvent size={14} stroke={1.8} aria-hidden="true" /> : <IconFileAlert size={14} stroke={1.8} aria-hidden="true" />}
      {num}
    </button>
  );

  return (
    <Page kind="table">
      <PageHeader
        title={tr('prc.title')}
        meta={ready || state === 'error' ? <>{chip('wait', n.wait)}{chip('exp', n.exp)}{chip('below', n.below)}</> : undefined}
        actions={
          <>
            <Segmented label={tr('prc.roleView')} value={role} onChange={(k) => setRole(k as 'sale' | 'mgr')} items={[{ key: 'sale', label: 'Sale' }, { key: 'mgr', label: tr('prc.mgr') }]} />
            <HeaderTool label={tr('prc.imp')} icon={IconUpload} onClick={noWrite} />
            <HeaderTool label={tr('prc.exp')} icon={IconDownload} onClick={soonTo(tr('prc.exp'))} />
            <HeaderTool label={tr('prc.history')} icon={IconHistory} onClick={soonTo(tr('prc.history'))} />
            <HeaderTool label={tr('prc.compare')} icon={IconArrowsDiff} onClick={soonTo(tr('prc.compare'))} />
            <Button variant="primary" icon={<IconPlus size={16} aria-hidden="true" />} disabled={noperm} onClick={noWrite}>{tr('prc.add')}</Button>
          </>
        }
      />
      <StateView
        state={state === 'noperm' || state === 'empty' ? 'ready' : state}
        title={state === 'empty' ? tr('prc.empty') : undefined}
        desc={state === 'empty' ? tr('prc.emptyDesc') : undefined}
        icon={state === 'empty' ? <IconFileText size={26} stroke={1.8} /> : undefined}
        action={state === 'error' ? <Button onClick={() => void Promise.all([linesQ.refetch(), cusQ.refetch(), surQ.refetch(), rtQ.refetch()])}>{tr('prc.retry')}</Button> : undefined}
        skeleton={<Skeleton rows={9} />}
      >
        {noperm ? (
          <section className={s.none} role="status">
            <IconLock size={28} stroke={1.8} aria-hidden="true" />
            <b>{tr('prc.noperm')}</b>
          </section>
        ) : (
          <div className={s.body}>
            <nav className={s.tree} aria-label={tr('prc.service')}>
              {tree.map((g) => {
                const c = groupCount(lines, cus, g.k);
                const on = g.k === grp;
                return (
                  <div key={g.k}>
                    <button type="button" className={`${s.node} ${on ? s.nodeOn : ''}`} aria-current={on} onClick={() => pickGrp(g.k)}>
                      <span className={s.nIc} style={g.k === 'all' ? { background: 'var(--sub)', color: g.color } : { ...tile(g.color), color: 'var(--tx2)' }}>
                        <g.Ico size={15} stroke={1.8} aria-hidden="true" />
                      </span>
                      <span className={s.ell}>{g.name}</span>
                      {c.wait > 0 && (
                        <span className={s.wait} title={tr('prc.f.wait')} aria-label={tr('prc.waitN', { n: c.wait })}>
                          <IconClockHour4 size={13} stroke={1.8} aria-hidden="true" />
                          {c.wait}
                        </span>
                      )}
                      <span className={s.lb}>{c.n}</span>
                    </button>
                    {on && g.k !== 'all' && codesOf(lines, g.k).map((cd) => (
                      <button key={cd} type="button" className={`${s.kid} ${code === cd ? s.kidOn : ''}`} aria-current={code === cd} onClick={() => setCode(code === cd ? 'all' : cd)}>
                        <b>{cd}</b>
                        <span className={`${s.ell} ${s.lb}`}>{lines.find((x) => x.code === cd)?.name}</span>
                        <span className={s.lb}>{lines.filter((x) => x.code === cd).length}</span>
                      </button>
                    ))}
                  </div>
                );
              })}
            </nav>
            <section className={s.pane} aria-label={tr('prc.paneLabel', { name: svName })}>
              <div className={s.pbar}>
                <Segmented
                  label={tr('prc.table')}
                  value={tab}
                  onChange={pickTab}
                  items={[{ key: 'std', label: tr('prc.t.std') }, { key: 'cus', label: tr('prc.t.cus') }, { key: 'sur', label: tr('prc.t.sur') }, { key: 'trf', label: 'Trucking' }]}
                />
                <span className={s.lb} title={tr('prc.rows')}>{svName} · {nRows}</span>
                <span className={s.right}>
                  {qf !== 'all' && (
                    <button type="button" className={s.qfx} aria-label={tr('prc.unfilter', { f: tr(FILTER[qf]) })} title={tr('prc.unfilterT')} onClick={() => setQf('all')}>
                      {tr(FILTER[qf])}
                      <IconX size={12} stroke={2.4} aria-hidden="true" />
                    </button>
                  )}
                  {tab === 'std' && (
                    <Segmented label={tr('prc.versions')} value={all ? 'all' : 'cur'} onChange={(k) => setAll(k === 'all')} items={[{ key: 'cur', label: tr('prc.eff') }, { key: 'all', label: tr('prc.allVers') }]} />
                  )}
                  {tab === 'trf' && (
                    <a className={s.open} href="/ezy/route-price" onClick={link(tr('prc.routePrice'))}>
                      <IconExternalLink size={16} stroke={1.8} aria-hidden="true" />
                      {tr('prc.openCalc')}
                    </a>
                  )}
                </span>
              </div>
              <div className={s.scroll}>
                {tab === 'std' && byGroup(std, (r) => r.n.grp).map((g) => (
                  <div key={g.k}>
                    <div className={s.gh}><h3>{g.name}</h3><span className={s.lb}>{g.rows.length}</span></div>
                    <div className={`${s.hd} ${colsStd}`} aria-hidden="true">
                      <span>{tr('prc.c.code')}</span><span>{tr('prc.c.fee')}</span><span>{tr('prc.c.unit')}</span><span>{tr('prc.c.calc')}</span><span className={s.r}>{tr('prc.c.std')}</span>
                      {mgr && <span className={s.r}>{tr('prc.c.floor')}</span>}
                      <span className={s.r}>VAT</span><span>{tr('prc.c.incl')}</span><span>{tr('prc.eff')}</span>
                    </div>
                    {g.rows.map(({ n: ln, x, old }) => {
                      const sc2 = surRows(surQ.data?.items ?? [], { grp: 'all', code: ln.code }).length;
                      const nx = old ? null : ln.next;
                      return (
                        <button key={`${ln.id}-${x.v}`} type="button" className={`${s.row} ${colsStd}`} style={{ opacity: old ? 0.62 : 1 }} aria-label={tr('prc.seePrice', { code: ln.code, name: ln.name, unit: UNIT[ln.unit] })} onClick={ln.route ? () => pickTab('trf') : soonTo(tr('prc.priceDetail'))}>
                          <span className={s.flex}><Code code={ln.code} grp={ln.grp} /></span>
                          <b className={s.clamp} title={ln.name}>{ln.name}</b>
                          <span className={s.tx2}>{UNIT[ln.unit]}</span>
                          <span className={s.calc} title={tr('prc.calcT', { v: CALC[ln.calc][0] })}>{CALC[ln.calc][1]}</span>
                          <b className={s.rb}>{priceText(ln, x)}</b>
                          {mgr && <span className={`${s.r} ${s.tx2}`} title={tr('prc.floorT')}>{floorText(ln, x)}</span>}
                          <span className={`${s.r} ${s.tx2}`}>{ln.vat}%</span>
                          <span className={s.incl}>
                            <span className={s.clamp2} title={ln.incl ? tr('prc.inclT', { v: ln.incl }) : tr('prc.noIncl')}>{ln.incl || '—'}</span>
                            {sc2 > 0 && <span className={s.sur} title={tr('prc.surN', { n: sc2 })} aria-label={tr('prc.surN', { n: sc2 })}>+{sc2}</span>}
                          </span>
                          <span className={s.col}>
                            <span className={s.flex}>
                              <StChip k={old ? x.st : ln.stx} />
                              {nx && (
                                <span role="img" className={s.nx} style={{ color: ST[nx.st][1] }} title={tr('prc.nextTip', { v: nx.v, st: ST[nx.st][0], from: dk(nx.from), p: priceText(ln, nx) })} aria-label={tr('prc.nextTip', { v: nx.v, st: ST[nx.st][0], from: dk(nx.from), p: priceText(ln, nx) })}>
                                  <IconCalendarEvent size={14} stroke={1.8} aria-hidden="true" />
                                </span>
                              )}
                            </span>
                            <span className={s.lb}>{x.v} · {dk(x.from)} → {dk(x.to)}</span>
                          </span>
                        </button>
                      );
                    })}
                  </div>
                ))}
                {tab === 'std' && std.length === 0 && <div className={s.empty}>{tr('prc.none')}</div>}

                {tab === 'cus' && byGroup(cusV, (r) => r.ln.grp).map((g) => (
                  <div key={g.k}>
                    <div className={s.gh}><h3>{g.name}</h3><span className={s.lb}>{g.rows.length}</span></div>
                    <div className={`${s.hd} ${colsCus}`} aria-hidden="true">
                      <span>{tr('prc.c.cus')}</span><span>{tr('prc.c.code')}</span><span>{tr('prc.service')}</span><span className={s.r}>{tr('prc.c.cusPrice')}</span><span className={s.r}>{tr('prc.c.std')}</span><span className={s.r}>{tr('prc.c.diff')}</span>
                      {mgr && <span className={s.r}>{tr('prc.c.floor')}</span>}
                      <span>{tr('prc.eff')}</span>
                    </div>
                    {g.rows.map((c) => {
                      const flag = c.under ? (mgr ? tr('prc.below') : c.st === 'wait' ? tr('prc.needApr') : tr('prc.apr')) : '';
                      const flagT = c.under
                        ? mgr ? tr('prc.belowT', { floor: floorText(c.ln, c.ln.cur), who: c.st === 'wait' ? tr('prc.waitFor', { s: SEAT[c.seat] }) : tr('prc.aprBy', { s: SEAT[c.by || 'mgr'] }) }) : flag
                        : '';
                      const d = c.ln.cur.std ? (c.price - c.ln.cur.std) / c.ln.cur.std : 0;
                      return (
                        <div key={c.id} className={`${s.row} ${s.rowDiv} ${colsCus}`} onClick={soonTo(tr('prc.cusDetail'))}>
                          <span className={s.who}>
                            <button type="button" className={s.cusBtn} aria-label={tr('prc.seeCus', { cus: c.cus, code: c.ln.code })}>{c.cus}</button>
                            <a className={s.ct} href="/ezy/contracts" title={tr('prc.openCt', { ct: c.ct })} aria-label={tr('prc.openCt', { ct: c.ct })} onClick={link(tr('prc.contract'))}>
                              <IconFileText size={13} stroke={1.8} aria-hidden="true" />
                              {c.ct}
                            </a>
                          </span>
                          <span className={s.flex}><Code code={c.ln.code} grp={c.ln.grp} /></span>
                          <span className={s.svc}><span className={s.ell} title={c.ln.name}>{c.ln.name}</span><span className={s.lb}>{UNIT[c.ln.unit]}</span></span>
                          <b className={s.rb}>{vnd(c.price)}</b>
                          <span className={`${s.r} ${s.tx2}`}>{priceText(c.ln, c.ln.cur)}</span>
                          <span className={s.r} style={{ color: d < 0 ? 'var(--warn)' : 'var(--tx2)', fontWeight: 'var(--fw-label)' }} title={tr('prc.vsStd')}>{deltaText(c)}</span>
                          {mgr && <span className={`${s.r} ${s.tx2}`}>{floorText(c.ln, c.ln.cur)}</span>}
                          <span className={s.col}>
                            <span className={`${s.flex} ${s.wrap}`}>
                              <StChip k={c.stx} />
                              {flag && (
                                <span role="img" className={s.flag} title={flagT} aria-label={flagT}>
                                  <IconAlertTriangle size={12} stroke={1.8} aria-hidden="true" />
                                  {flag}
                                </span>
                              )}
                            </span>
                            <span className={s.lb}>{dk(c.from)} → {dk(c.to)}</span>
                          </span>
                        </div>
                      );
                    })}
                  </div>
                ))}
                {tab === 'cus' && cusV.length === 0 && <div className={s.empty}>{tr('prc.noCus')}</div>}

                {tab === 'sur' && (
                  <div className={s.surWrap}>
                    <div className={s.surMain}>
                      <div className={`${s.hd} ${s.cSur}`} aria-hidden="true">
                        <span>{tr('prc.c.appl')}</span><span>{tr('prc.c.item')}</span><span>{tr('prc.c.formula')}</span><span className={s.r}>{tr('prc.c.unitPrice')}</span><span>{tr('prc.c.status')}</span>
                      </div>
                      {surV.map((r) => (
                        <div key={r.id} className={`${s.row} ${s.rowDiv} ${s.cSur}`}>
                          <span className={s.flex}><Code code={r.code} grp={r.grp} w={52} /></span>
                          <b className={s.nm}>{r.name}</b>
                          <span className={s.tx2}>{r.formula.replace('{p}', r.price ? vnd(r.price) : '')}</span>
                          <b className={s.rb}>{r.price ? vnd(r.price) : '—'}</b>
                          <span><StChip k={r.st} /></span>
                        </div>
                      ))}
                      {surV.length === 0 && <div className={s.empty}>{tr('prc.noSur')}</div>}
                    </div>
                    <section className={s.calcBox} aria-label={tr('prc.trialAria')}>
                      <div className={s.flex}><IconPlayerPlay size={16} stroke={1.8} aria-hidden="true" /><h3>{tr('prc.trial')}</h3></div>
                      <div className={s.inputs}>
                        {([['h', tr('prc.in.h')], ['w', tr('prc.in.w')], ['wh', tr('prc.in.wh')], ['ex', 'FPU · TRA · XLH ₫']] as const).map(([k, label]) => (
                          <label key={k}>
                            <span>{label}</span>
                            <input aria-label={label} inputMode="decimal" value={ci[k]} onChange={setIn(k)} />
                          </label>
                        ))}
                      </div>
                      <div>
                        <Button variant="primary" icon={<IconPlayerPlay size={16} aria-hidden="true" />} onClick={() => setCalc(calcSur(ci))}>{tr('prc.calc')}</Button>
                      </div>
                      {calc && (
                        <div className={s.res} role="status">
                          {calc.rows.map(([k, f, val]) => (
                            <div key={k} className={s.resRow}>
                              <span className={s.fw}>{k}</span>
                              <b style={{ color: val ? 'var(--tx)' : 'var(--lb)' }}>{vnd(val)}</b>
                              <span className={s.lb}>{f}</span>
                            </div>
                          ))}
                          <div className={s.resSum}><span className={s.fw}>{tr('prc.sur')}</span><b>{vnd(calc.sum)}</b></div>
                        </div>
                      )}
                    </section>
                  </div>
                )}

                {tab === 'trf' && (
                  <div>
                    <div className={`${s.hd} ${colsTrf}`} aria-hidden="true">
                      <span>{tr('prc.c.route')}</span><span className={s.r}>Km</span><span className={s.r}>TRF 20 ₫</span><span className={s.r}>TRF 40 ₫</span><span className={s.r}>TRFA 40 ₫</span>
                      {mgr && <span className={s.r}>{tr('prc.floor40')}</span>}
                      <span>{tr('prc.eff')}</span><span />
                    </div>
                    {trfV.map((r) => {
                      const lab = tr('prc.openRoute', { from: r.from, to: r.to });
                      return (
                        <div key={r.id} className={`${s.row} ${s.rowDiv} ${colsTrf}`}>
                          <span className={s.route}><b>{r.from}</b><span className={s.lb} aria-label={tr('prc.toWord')}>→</span><span className={s.ell}>{r.to}</span></span>
                          <span className={`${s.r} ${s.tx2}`}>{r.km} km</span>
                          <span className={s.r}>{vnd(r.a20)}</span>
                          <span className={s.r}>{vnd(r.a40)}</span>
                          <b className={s.rb}>{vnd(r.aA)}</b>
                          {mgr && <span className={`${s.r} ${s.tx2}`}>{vnd(r.flo)}</span>}
                          <span className={s.col}><StChip k="on" /><span className={s.lb}>{dk(r.since)} → —</span></span>
                          <a className={s.go} href="/ezy/route-price" aria-label={lab} title={lab} onClick={link(tr('prc.routePrice'))}><IconExternalLink size={15} stroke={1.8} aria-hidden="true" /></a>
                        </div>
                      );
                    })}
                    {trfV.length === 0 && <div className={s.empty}>{tr('prc.noRoute')}</div>}
                  </div>
                )}
              </div>
            </section>
          </div>
        )}
      </StateView>
    </Page>
  );
}

export function Price() {
  return useLayout() === 'pad' ? <PriceCompact /> : <PriceTable />;
}
